/**
 * Fullscreen Inactivity Session Lock Overlay
 * 
 * Enforces HIPAA § 164.312(a)(2)(iii) Technical Safeguard:
 * Prevents unauthorized viewing of electronic Protected Health Information (ePHI)
 * and sensitive client habit data on unattended workstations.
 * 
 * Displays cryptographic zeroization status and requires PIN re-authentication.
 */

import React, { useState, useEffect, useRef } from 'react';

export interface InactivityLockOverlayProps {
  isLocked: boolean;
  onUnlock: (pin: string) => boolean;
  defaultPinHint?: string;
  onSignOut?: () => void;
  lockTimestamp?: number;
}

export const InactivityLockOverlay: React.FC<InactivityLockOverlayProps> = ({
  isLocked,
  onUnlock,
  defaultPinHint = 'Default demo PIN: 1234',
  onSignOut,
  lockTimestamp,
}) => {
  const [pinInput, setPinInput] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [attempts, setAttempts] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isLocked) {
      setPinInput('');
      setErrorMessage(null);
      setAttempts(0);
      // Auto-focus input when lock appears
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isLocked]);

  if (!isLocked) return null;

  const handleUnlock = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!pinInput.trim()) {
      setErrorMessage('Please enter your session unlock PIN.');
      return;
    }

    const success = onUnlock(pinInput.trim());
    if (success) {
      setPinInput('');
      setErrorMessage(null);
      setAttempts(0);
    } else {
      setAttempts((prev) => prev + 1);
      setErrorMessage('Incorrect unlock PIN. Please re-enter your authorized credentials.');
      inputRef.current?.select();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="HIPAA Inactivity Session Lock"
      className="inactivity-lock-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.94)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          maxWidth: '480px',
          width: '100%',
          padding: '36px 32px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          border: '1px solid #E2E8F0',
          textAlign: 'center',
        }}
      >
        {/* Shield & Lock Emblem */}
        <div
          style={{
            width: '68px',
            height: '68px',
            borderRadius: '50%',
            backgroundColor: '#FEF3C7',
            border: '2px solid #FCD34D',
            color: '#B45309',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px auto',
            fontSize: '32px',
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)',
          }}
        >
          🔒
        </div>

        {/* Title and Regulation Citation */}
        <h2
          style={{
            margin: '0 0 8px 0',
            fontSize: '22px',
            fontWeight: 800,
            color: '#0F172A',
            letterSpacing: '-0.4px',
          }}
        >
          Session Locked for ePHI Protection
        </h2>

        <div
          style={{
            display: 'inline-block',
            backgroundColor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            color: '#1E40AF',
            fontSize: '11px',
            fontWeight: 700,
            padding: '4px 10px',
            borderRadius: '6px',
            marginBottom: '16px',
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
          }}
        >
          HIPAA Security Rule § 164.312(a)(2)(iii)
        </div>

        {/* Rationale and RAM Zeroization Details */}
        <p
          style={{
            margin: '0 0 16px 0',
            fontSize: '13px',
            lineHeight: 1.6,
            color: '#475569',
            textAlign: 'left',
          }}
        >
          This workstation has been idle for 15 minutes. To safeguard electronic Protected Health Information (ePHI) from unauthorized shoulder-surfing on unattended screens, the active session has been locked.
        </p>

        <div
          style={{
            backgroundColor: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: '22px',
            textAlign: 'left',
            fontSize: '12px',
            color: '#334155',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ color: '#10B981', fontWeight: 800 }}>✓</span>
            <span style={{ fontWeight: 700 }}>Volatile RAM Zeroization Executed</span>
          </div>
          <p style={{ margin: 0, fontSize: '11px', color: '#64748B', lineHeight: 1.4 }}>
            Decrypted client records, cached journals, and temporary cryptographic buffers were wiped from browser memory.
          </p>
        </div>

        {/* PIN Unlock Form */}
        <form onSubmit={handleUnlock} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ textAlign: 'left' }}>
            <label
              htmlFor="inactivity-unlock-pin"
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 700,
                color: '#334155',
                marginBottom: '6px',
              }}
            >
              Enter Unlock PIN / Passphrase
            </label>
            <input
              id="inactivity-unlock-pin"
              ref={inputRef}
              type="password"
              value={pinInput}
              onChange={(e) => {
                setPinInput(e.target.value);
                if (errorMessage) setErrorMessage(null);
              }}
              placeholder="••••"
              maxLength={32}
              autoComplete="off"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '12px 16px',
                fontSize: '16px',
                letterSpacing: '3px',
                borderRadius: '10px',
                border: errorMessage ? '2px solid #EF4444' : '1px solid #CBD5E1',
                outline: 'none',
                textAlign: 'center',
                backgroundColor: '#FFFFFF',
                color: '#0F172A',
                fontWeight: 700,
                transition: 'border-color 0.15s ease',
              }}
            />
          </div>

          {errorMessage && (
            <div
              role="alert"
              style={{
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                color: '#991B1B',
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 600,
                textAlign: 'left',
              }}
            >
              ⚠️ {errorMessage}
            </div>
          )}

          <div style={{ fontSize: '11px', color: '#94A3B8', textAlign: 'center' }}>
            {defaultPinHint}
          </div>

          <button
            type="submit"
            style={{
              backgroundColor: '#5E8276',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '10px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(94, 130, 118, 0.3)',
              transition: 'background-color 0.15s ease',
            }}
          >
            🔓 Unlock Workstation Session
          </button>
        </form>

        {onSignOut && (
          <div style={{ marginTop: '18px', borderTop: '1px solid #F1F5F9', paddingTop: '14px' }}>
            <button
              onClick={onSignOut}
              type="button"
              style={{
                background: 'none',
                border: 'none',
                color: '#64748B',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Return to Login Screen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
